import type { AiMarketPanel, OpenRouterMarketMonth, OpenRouterModelTapeRow, OpenRouterTaskMixRow } from "../types";

type RankingRow = { date: string; model_permaslug: string; total_tokens: string };
type BenchmarkRow = {
  source: "artificial-analysis";
  model_permaslug: string;
  display_name: string;
  intelligence_index: number | null;
  coding_index?: number | null;
  agentic_index?: number | null;
  pricing?: { prompt?: string; completion?: string };
};

export interface AiMarketRawInput {
  rankingsJson: string;
  appRankingsJson?: string;
  classificationsJson?: string;
  benchmarksJson?: string;
  asOf: string;
}

const MIN_BENCHMARK_COVERAGE_PCT = 25;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isoDate(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error(`${label} must be YYYY-MM-DD.`);
  const parsed = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) throw new Error(`${label} is not a calendar date.`);
}

function finiteFraction(value: unknown, label: string) {
  if (!Number.isFinite(value) || Number(value) < 0 || Number(value) > 1) throw new Error(`${label} must be between zero and one.`);
  return Number(value);
}

function percentage(numerator: bigint, denominator: bigint) {
  if (denominator <= 0n) return 0;
  return Number(numerator * 1_000_000n / denominator) / 10_000;
}

function ratio(numerator: bigint, denominator: bigint) {
  if (denominator <= 0n) return null;
  const whole = numerator / denominator;
  const remainder = numerator % denominator;
  return Number(whole) + Number(remainder) / Number(denominator);
}

function modelLabel(slug: string) {
  const name = slug.split("/").at(-1) ?? slug;
  return name.replace(/[-_:]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function parseRankings(json: string, asOf: string) {
  const payload: unknown = JSON.parse(json);
  if (!isRecord(payload) || !Array.isArray(payload.data) || !isRecord(payload.meta) || payload.meta.version !== "v1") {
    throw new Error("OpenRouter rankings market input has an invalid schema/version.");
  }
  isoDate(asOf, "AI market asOf");
  const rows: RankingRow[] = [];
  const seen = new Set<string>();
  for (const [index, item] of payload.data.entries()) {
    if (!isRecord(item)) throw new Error(`OpenRouter rankings row ${index + 1} is not an object.`);
    isoDate(item.date, `OpenRouter rankings row ${index + 1} date`);
    if (typeof item.model_permaslug !== "string" || !item.model_permaslug || typeof item.total_tokens !== "string" || !/^\d+$/.test(item.total_tokens)) {
      throw new Error(`OpenRouter rankings row ${index + 1} has invalid identity or Tokens.`);
    }
    if (item.date.slice(0, 7) >= asOf.slice(0, 7)) continue;
    const key = `${item.date}|${item.model_permaslug}`;
    if (seen.has(key)) throw new Error(`OpenRouter rankings duplicate ${key}.`);
    seen.add(key);
    rows.push(item as unknown as RankingRow);
  }
  if (!rows.length || typeof payload.meta.as_of !== "string") throw new Error("OpenRouter rankings market input is empty or lacks as_of.");
  return { rows, asOf: payload.meta.as_of };
}

function parseApps(json: string | undefined) {
  if (!json) return new Map<string, { tokensPerRequest: number; requests: number; count: number }>();
  const payload: unknown = JSON.parse(json);
  if (!isRecord(payload) || payload.version !== "v1" || !Array.isArray(payload.months)) throw new Error("OpenRouter app rankings bundle has an invalid schema/version.");
  const output = new Map<string, { tokensPerRequest: number; requests: number; count: number }>();
  for (const [index, item] of payload.months.entries()) {
    if (!isRecord(item) || typeof item.month !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(item.month) || !Array.isArray(item.apps)) {
      throw new Error(`OpenRouter app month ${index + 1} is invalid.`);
    }
    let tokens = 0n;
    let requests = 0;
    for (const [appIndex, app] of item.apps.entries()) {
      if (!isRecord(app) || !Number.isSafeInteger(app.total_requests) || Number(app.total_requests) <= 0
        || typeof app.total_tokens !== "string" || !/^\d+$/.test(app.total_tokens)) {
        throw new Error(`OpenRouter app month ${item.month} row ${appIndex + 1} is invalid.`);
      }
      requests += Number(app.total_requests);
      tokens += BigInt(app.total_tokens);
    }
    const tokensPerRequest = ratio(tokens, BigInt(requests));
    if (tokensPerRequest == null) throw new Error(`OpenRouter app month ${item.month} has no requests.`);
    output.set(item.month, { tokensPerRequest, requests, count: item.apps.length });
  }
  return output;
}

function parseBenchmarks(json: string | undefined) {
  const empty = { map: new Map<string, BenchmarkRow>(), asOf: null as string | null, citation: null as string | null };
  if (!json) return empty;
  const payload: unknown = JSON.parse(json);
  if (!isRecord(payload) || !Array.isArray(payload.data) || !isRecord(payload.meta) || payload.meta.version !== "v1") {
    throw new Error("OpenRouter benchmark payload has an invalid schema/version.");
  }
  const map = new Map<string, BenchmarkRow>();
  for (const [index, item] of payload.data.entries()) {
    if (!isRecord(item) || item.source !== "artificial-analysis" || typeof item.model_permaslug !== "string" || !item.model_permaslug) {
      throw new Error(`OpenRouter benchmark row ${index + 1} is invalid.`);
    }
    const intelligence = item.intelligence_index;
    if (intelligence != null && !Number.isFinite(intelligence)) throw new Error(`OpenRouter benchmark row ${index + 1} intelligence is invalid.`);
    if (map.has(item.model_permaslug)) throw new Error(`Duplicate OpenRouter benchmark ${item.model_permaslug}.`);
    map.set(item.model_permaslug, item as unknown as BenchmarkRow);
  }
  return {
    map,
    asOf: typeof payload.meta.as_of === "string" ? payload.meta.as_of : null,
    citation: typeof payload.meta.citation === "string" ? payload.meta.citation : null,
  };
}

function parseTaskMix(json: string | undefined): AiMarketPanel["taskMix"] {
  if (!json) return { asOf: null, windowDays: null, rows: [] };
  const payload: unknown = JSON.parse(json);
  if (!isRecord(payload) || !isRecord(payload.data) || !Array.isArray(payload.data.macro_categories)) {
    throw new Error("OpenRouter task classification payload has an invalid schema.");
  }
  const rows: OpenRouterTaskMixRow[] = payload.data.macro_categories.map((item, index) => {
    if (!isRecord(item) || typeof item.key !== "string" || typeof item.label !== "string") {
      throw new Error(`OpenRouter task classification ${index + 1} has invalid identity.`);
    }
    const tokenShare = finiteFraction(item.token_share, `${item.key} token_share`);
    const requestShare = finiteFraction(item.usage_share, `${item.key} usage_share`);
    return {
      key: item.key,
      label: item.label,
      tokenSharePct: tokenShare * 100,
      requestSharePct: requestShare * 100,
      tokenIntensity: requestShare > 0 ? tokenShare / requestShare : 0,
    };
  }).sort((left, right) => right.tokenSharePct - left.tokenSharePct);
  return {
    asOf: typeof payload.data.as_of === "string" ? payload.data.as_of : null,
    windowDays: Number.isInteger(payload.data.window_days) ? Number(payload.data.window_days) : null,
    rows,
  };
}

export function buildAiMarketPanel(input: AiMarketRawInput): AiMarketPanel {
  const rankings = parseRankings(input.rankingsJson, input.asOf);
  const apps = parseApps(input.appRankingsJson);
  const benchmarks = parseBenchmarks(input.benchmarksJson);
  const firstSeen = new Map<string, string>();
  for (const row of rankings.rows) {
    if (row.model_permaslug !== "other" && !firstSeen.has(row.model_permaslug)) firstSeen.set(row.model_permaslug, row.date.slice(0, 7));
  }
  const byMonth = new Map<string, RankingRow[]>();
  for (const row of rankings.rows) {
    const month = row.date.slice(0, 7);
    byMonth.set(month, [...(byMonth.get(month) ?? []), row]);
  }
  const earliestMonth = [...byMonth.keys()].sort()[0];
  const history: OpenRouterMarketMonth[] = [...byMonth.entries()].sort((left, right) => left[0].localeCompare(right[0])).map(([month, rows]) => {
    let total = 0n;
    let tail = 0n;
    const models = new Map<string, bigint>();
    const byDay = new Map<string, Array<[string, bigint]>>();
    for (const row of rows) {
      const tokens = BigInt(row.total_tokens);
      total += tokens;
      if (row.model_permaslug === "other") tail += tokens;
      else models.set(row.model_permaslug, (models.get(row.model_permaslug) ?? 0n) + tokens);
      byDay.set(row.date, [...(byDay.get(row.date) ?? []), [row.model_permaslug, tokens]]);
    }
    if (total <= 0n || !models.size) throw new Error(`OpenRouter market month ${month} has no usable model traffic.`);
    const sorted = [...models.entries()].sort((left, right) => left[1] === right[1] ? left[0].localeCompare(right[0]) : left[1] > right[1] ? -1 : 1);
    const leaders = new Set<string>();
    for (const day of byDay.values()) {
      const leader = day.filter(([slug]) => slug !== "other").sort((left, right) => left[1] === right[1] ? left[0].localeCompare(right[0]) : left[1] > right[1] ? -1 : 1)[0];
      if (leader) leaders.add(leader[0]);
    }
    const top5 = sorted.slice(0, 5).reduce((sum, [, tokens]) => sum + tokens, 0n);
    const firstEntries = month === earliestMonth ? null : sorted.filter(([slug]) => firstSeen.get(slug) === month).reduce((sum, [, tokens]) => sum + tokens, 0n);
    let intelligenceTokens = 0n;
    let weightedIntelligence = 0;
    let priceTokens = 0n;
    let weightedCost = 0;
    let weightedInputPrice = 0;
    let weightedOutputPrice = 0;
    for (const [slug, tokens] of sorted) {
      const benchmark = benchmarks.map.get(slug);
      if (benchmark && Number.isFinite(benchmark.intelligence_index)) {
        intelligenceTokens += tokens;
        weightedIntelligence += Number(tokens) * Number(benchmark.intelligence_index);
      }
      const prompt = Number(benchmark?.pricing?.prompt);
      const completion = Number(benchmark?.pricing?.completion);
      if (Number.isFinite(prompt) && prompt >= 0 && Number.isFinite(completion) && completion >= 0) {
        priceTokens += tokens;
        weightedCost += Number(tokens) * (prompt * 10_000 + completion * 1_500);
        weightedInputPrice += Number(tokens) * prompt * 1_000_000;
        weightedOutputPrice += Number(tokens) * completion * 1_000_000;
      }
    }
    const app = apps.get(month);
    const leader = sorted[0];
    const leaderBenchmark = benchmarks.map.get(leader[0]);
    const intelligenceCoveragePct = intelligenceTokens > 0n ? percentage(intelligenceTokens, total) : null;
    const priceCoveragePct = priceTokens > 0n ? percentage(priceTokens, total) : null;
    return {
      month,
      totalTokensT: Number(total / 1_000_000n) / 1_000_000,
      leaderModelId: leader[0],
      leaderModelLabel: leaderBenchmark?.display_name || modelLabel(leader[0]),
      leaderSharePct: percentage(leader[1], total),
      top5SharePct: percentage(top5, total),
      tailSharePct: percentage(tail, total),
      rankedModelCount: models.size,
      dailyLeaderCount: leaders.size,
      firstEntryTokenSharePct: firstEntries == null ? null : percentage(firstEntries, total),
      publicAppTokensPerRequest: app?.tokensPerRequest ?? null,
      publicAppRequests: app?.requests ?? null,
      publicAppCount: app?.count ?? null,
      weightedIntelligenceIndex: intelligenceCoveragePct != null && intelligenceCoveragePct >= MIN_BENCHMARK_COVERAGE_PCT
        ? weightedIntelligence / Number(intelligenceTokens)
        : null,
      intelligenceCoveragePct,
      standardCallCostUsd: priceCoveragePct != null && priceCoveragePct >= MIN_BENCHMARK_COVERAGE_PCT
        ? weightedCost / Number(priceTokens)
        : null,
      weightedInputPricePerMillionUsd: priceCoveragePct != null && priceCoveragePct >= MIN_BENCHMARK_COVERAGE_PCT
        ? weightedInputPrice / Number(priceTokens)
        : null,
      weightedOutputPricePerMillionUsd: priceCoveragePct != null && priceCoveragePct >= MIN_BENCHMARK_COVERAGE_PCT
        ? weightedOutputPrice / Number(priceTokens)
        : null,
      priceCoveragePct,
    };
  });
  const tapeMonth = history.at(-1)?.month;
  const tapeRows = tapeMonth ? rankings.rows.filter((row) => row.date.slice(0, 7) === tapeMonth) : [];
  const tapeTotal = tapeRows.reduce((sum, row) => sum + BigInt(row.total_tokens), 0n);
  const tapeModels = new Map<string, bigint>();
  for (const row of tapeRows) {
    if (row.model_permaslug === "other") continue;
    tapeModels.set(row.model_permaslug, (tapeModels.get(row.model_permaslug) ?? 0n) + BigInt(row.total_tokens));
  }
  const modelTapeRows: OpenRouterModelTapeRow[] = [...tapeModels.entries()]
    .sort((left, right) => left[1] === right[1] ? left[0].localeCompare(right[0]) : left[1] > right[1] ? -1 : 1)
    .slice(0, 12)
    .map(([modelId, tokens]) => {
      const benchmark = benchmarks.map.get(modelId);
      const prompt = Number(benchmark?.pricing?.prompt);
      const completion = Number(benchmark?.pricing?.completion);
      return {
        modelId,
        label: benchmark?.display_name || modelLabel(modelId),
        tokenSharePct: percentage(tokens, tapeTotal),
        intelligenceIndex: Number.isFinite(benchmark?.intelligence_index) ? Number(benchmark?.intelligence_index) : null,
        inputPricePerMillionUsd: Number.isFinite(prompt) && prompt >= 0 ? prompt * 1_000_000 : null,
        outputPricePerMillionUsd: Number.isFinite(completion) && completion >= 0 ? completion * 1_000_000 : null,
        publicParametersB: null,
      };
    });
  return {
    asOf: input.asOf,
    rankingsAsOf: rankings.asOf,
    benchmarkAsOf: benchmarks.asOf,
    benchmarkCitation: benchmarks.citation,
    benchmarkMethod: benchmarks.map.size ? "current-aa-rescored-history" : "unavailable",
    standardCall: { inputTokens: 10_000, outputTokens: 1_500 },
    history,
    taskMix: parseTaskMix(input.classificationsJson),
    modelTape: tapeMonth ? { month: tapeMonth, rows: modelTapeRows } : undefined,
    limitations: [
      "OpenRouter rankings cover one routing platform; daily top 50 plus the aggregated other row are not a global model market.",
      "Tokens use each upstream provider's tokenizer and are not perfectly comparable across model families.",
      "Tokens per request cover the returned top public apps; hidden/private apps and the long tail are outside that denominator.",
      "Historical intelligence is re-scored with the current Artificial Analysis index version; it is not a point-in-time benchmark vintage.",
      `Intelligence and price indices are suppressed below ${MIN_BENCHMARK_COVERAGE_PCT}% of monthly platform Tokens.`,
      "Standard-call cost applies current list prices to 10k input plus 1.5k output Tokens; it is not observed customer spend per request.",
    ],
  };
}

export function parseAiMarketPanel(value: unknown): AiMarketPanel {
  if (!isRecord(value) || typeof value.asOf !== "string" || typeof value.rankingsAsOf !== "string"
    || !isRecord(value.standardCall) || value.standardCall.inputTokens !== 10_000 || value.standardCall.outputTokens !== 1_500
    || !Array.isArray(value.history) || value.history.length < 12 || !isRecord(value.taskMix)
    || !Array.isArray(value.taskMix.rows) || !Array.isArray(value.limitations)) {
    throw new Error("AI market evidence has an invalid schema.");
  }
  isoDate(value.asOf, "AI market evidence asOf");
  const seen = new Set<string>();
  let priorMonth = "";
  for (const [index, item] of value.history.entries()) {
    if (!isRecord(item) || typeof item.month !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(item.month)
      || item.month <= priorMonth || seen.has(item.month) || typeof item.leaderModelId !== "string" || !item.leaderModelId
      || typeof item.leaderModelLabel !== "string" || !item.leaderModelLabel) {
      throw new Error(`AI market evidence month ${index + 1} is invalid or unsorted.`);
    }
    seen.add(item.month);
    priorMonth = item.month;
    for (const field of ["totalTokensT", "leaderSharePct", "top5SharePct", "tailSharePct", "rankedModelCount", "dailyLeaderCount"] as const) {
      if (!Number.isFinite(item[field]) || Number(item[field]) < 0) throw new Error(`AI market evidence ${item.month} ${field} is invalid.`);
    }
    for (const field of ["firstEntryTokenSharePct", "publicAppTokensPerRequest", "publicAppRequests", "publicAppCount", "weightedIntelligenceIndex", "intelligenceCoveragePct", "standardCallCostUsd", "priceCoveragePct"] as const) {
      if (item[field] != null && (!Number.isFinite(item[field]) || Number(item[field]) < 0)) throw new Error(`AI market evidence ${item.month} ${field} is invalid.`);
    }
    for (const field of ["weightedInputPricePerMillionUsd", "weightedOutputPricePerMillionUsd"] as const) {
      if (item[field] != null && (!Number.isFinite(item[field]) || Number(item[field]) < 0)) throw new Error(`AI market evidence ${item.month} ${field} is invalid.`);
    }
    if (Number(item.leaderSharePct) > Number(item.top5SharePct) || Number(item.top5SharePct) > 100 || Number(item.tailSharePct) > 100) {
      throw new Error(`AI market evidence ${item.month} concentration is inconsistent.`);
    }
  }
  for (const [index, item] of value.taskMix.rows.entries()) {
    if (!isRecord(item) || typeof item.key !== "string" || typeof item.label !== "string"
      || !Number.isFinite(item.tokenSharePct) || !Number.isFinite(item.requestSharePct) || !Number.isFinite(item.tokenIntensity)) {
      throw new Error(`AI market evidence task row ${index + 1} is invalid.`);
    }
  }
  if (value.modelTape != null) {
    if (!isRecord(value.modelTape) || typeof value.modelTape.month !== "string" || !Array.isArray(value.modelTape.rows)) {
      throw new Error("AI market evidence model tape is invalid.");
    }
    for (const [index, item] of value.modelTape.rows.entries()) {
      if (!isRecord(item) || typeof item.modelId !== "string" || !item.modelId || typeof item.label !== "string" || !item.label
        || !Number.isFinite(item.tokenSharePct) || Number(item.tokenSharePct) < 0
        || (item.intelligenceIndex != null && !Number.isFinite(item.intelligenceIndex))
        || (item.inputPricePerMillionUsd != null && (!Number.isFinite(item.inputPricePerMillionUsd) || Number(item.inputPricePerMillionUsd) < 0))
        || (item.outputPricePerMillionUsd != null && (!Number.isFinite(item.outputPricePerMillionUsd) || Number(item.outputPricePerMillionUsd) < 0))
        || item.publicParametersB != null) {
        throw new Error(`AI market evidence model tape row ${index + 1} is invalid.`);
      }
    }
  }
  return value as unknown as AiMarketPanel;
}
